import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithReauth } from './baseQueryWithReauth';
import type { RootState } from '../index';

// Guard rails per TechAdds v2 spec: every customer request must include
// a valid tenant context so data never leaks across tenants. Ref: docs/Techadds_setup_and_timeline_v2.md lines 235-244.
function getTenantInfo() {
  let subdomain = 'default';
  let tenantId = 'unknown';
  try {
    if (typeof window !== 'undefined') {
      subdomain = window.localStorage.getItem('subdomain') || subdomain;
      tenantId = window.localStorage.getItem('tenantId') || tenantId;
      if (!tenantId) {
        const token = window.localStorage.getItem('token');
        if (token) {
          try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            tenantId = payload.tenantId || payload.tenant_id || tenantId;
          } catch {
            /* ignore */
          }
        }
      }
    }
  } catch {
    /* ignore */
  }
  return { tenantId, subdomain };
}

function currentTenantTag(): string {
  const { tenantId, subdomain } = getTenantInfo();
  return `${tenantId}:${subdomain}`;
}

export interface Customer {
  id: string;
  name: string;
}

export const customersApi = createApi({
  reducerPath: 'customersApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Customer'],
  keepUnusedDataFor: 60,
  endpoints: (builder) => ({
    getCustomers: builder.query<Customer[], { tenantId?: string; subdomain?: string; bust?: number } | void>({
      query: (arg) => {
        const bust = (arg as any)?.bust;
        const qs = bust ? `?cb=${bust}` : '';
        return { url: `/customers${qs}` };
      },
      providesTags: (_r, _e, arg) => {
        const info = arg as any;
        if (!info?.tenantId) {
          console.warn('[customersApi] tenantId undefined in getCustomers arg');
        }
        const tagId = `${info?.tenantId || getTenantInfo().tenantId}:${info?.subdomain || getTenantInfo().subdomain}`;
        return [{ type: 'Customer', id: tagId }];
      },
      async onQueryStarted(_, { queryFulfilled, getState }) {
        const state = getState() as RootState;
        console.log('[customersApi getCustomers start]', {
          token: state.auth?.token,
          subdomain: state.tenant?.subdomain,
          tenantId: state.auth?.user?.tenantId,
          cache: state.customersApi,
        });
        try {
          const { data } = await queryFulfilled;
          console.log('[customersApi getCustomers success]', { data });
        } catch (err) {
          console.log('[customersApi getCustomers error]', err);
        } finally {
          const endState = getState() as RootState;
          console.log('[customersApi getCustomers end state]', endState.customersApi);
        }
      },
    }),
    createCustomer: builder.mutation<Customer, Partial<Customer>>({
      query: (body) => ({
        url: '/customers',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, _a) => [{ type: 'Customer', id: currentTenantTag() }],
      async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
        const tag = { type: 'Customer', id: currentTenantTag() } as const;
        const bust = Date.now();
        const state = getState() as RootState;
        console.log('[customersApi createCustomer start]', {
          arg,
          token: state.auth?.token,
          subdomain: state.tenant?.subdomain,
          tenantId: state.auth?.user?.tenantId,
          cache: state.customersApi,
        });
        // force refetch and update after creation
        const refetchAction = customersApi.endpoints.getCustomers.initiate(
          { bust, tenantId: state.auth?.user?.tenantId, subdomain: state.tenant?.subdomain },
          { forceRefetch: true }
        );
        const refetchPromise = dispatch(refetchAction).unwrap();
        try {
          const { data } = await queryFulfilled;
          console.log('[customersApi createCustomer success]', data);
          const list = await refetchPromise;
          const found = Array.isArray(list) && list.some((c) => c.id === data.id);
          if (!found) {
            console.error('[createCustomer] customer missing after refetch', { data, list });
            if (typeof window !== 'undefined') {
              window.alert('Customer created but not found in list');
            }
          }
          dispatch(
            customersApi.util.updateQueryData(
              'getCustomers',
              { bust, tenantId: state.auth?.user?.tenantId, subdomain: state.tenant?.subdomain },
              (draft: Customer[]) => {
                draft.push(data as Customer);
              }
            )
          );
        } catch (err) {
          console.log('[customersApi createCustomer error]', err);
        } finally {
          dispatch(customersApi.util.invalidateTags([tag]));
          dispatch(
            customersApi.endpoints.getCustomers.initiate(
              { bust: Date.now(), tenantId: state.auth?.user?.tenantId, subdomain: state.tenant?.subdomain },
              {
                forceRefetch: true,
              }
            )
          );
          console.log('[customersApi createCustomer final state]', (getState() as RootState).customersApi);
        }
      },
    }),
  }),
});

export const { useGetCustomersQuery, useCreateCustomerMutation } = customersApi;
